import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, IconButton, Badge, SvgIcon, Tooltip } from '@mui/material'
import { MoreVert as MoreVertIcon } from '@mui/icons-material'
import { ClipboardList } from '@styled-icons/heroicons-outline'

import { get } from 'utils/lodash'
import { ReactComponent as CalendarWeek } from 'assets/CalendarWeek.svg'
import { useLazyFetchWorkOrderListQuery } from 'api/work-order/fetchWorkOrderList'
import { WORK_ORDER_STATUS_IDS } from 'components/customer-details/content/work-orders/filters/work-order-status-filter/settings'
import { DEFAULT_SORT_RULES_BY_SERVICE_DATE_DESC } from 'components/customer-details/content/work-orders/settings'
import T from 'T'
import { RECURRENCE } from 'settings/constants/service'
import UpcomingWorkOrdersPopover from 'components/customer-details/content/configured-services/actions/weekly-summary/UpcomingWorkOrdersPopover'
import LocationServicesPopover from './LocationServicesPopover'

const WO_PAGE_SIZE = 10

const LocationActionsColumn = ({ location, accountId, onLocationAnchorElChange, onSelectedLocationForMenuChange }) => {
  const serviceCount = get(location, 'serviceCount', 0)
  const locationId = get(location, 'id')
  const [servicesAnchorEl, setServicesAnchorEl] = useState(null)
  const [workOrdersAnchorEl, setWorkOrdersAnchorEl] = useState(null)

  const [fetchWorkOrderList, { data: workOrderListData, isFetching: isWorkOrdersLoading }] = useLazyFetchWorkOrderListQuery()

  const workOrders = useMemo(() => get(workOrderListData, 'workOrders', []), [workOrderListData])
  const workOrderCount = useMemo(() => get(workOrderListData, 'totalItems', null), [workOrderListData])

  useEffect(() => {
    if (!locationId) return
    fetchWorkOrderList({
      locationId,
      accountId,
      startDate: new Date().toISOString().split('T')[0],
      sortRules: [...DEFAULT_SORT_RULES_BY_SERVICE_DATE_DESC],
      statuses: WORK_ORDER_STATUS_IDS,
      requestedPage: 0,
      requestedPageSize: WO_PAGE_SIZE,
    })
  }, [locationId, accountId, fetchWorkOrderList])

  const handleWorkOrderStatusHover = e => {
    e.stopPropagation()
    setWorkOrdersAnchorEl(e.currentTarget)
  }

  const handleWorkOrderStatusLeave = () => {
    setWorkOrdersAnchorEl(null)
  }

  const handleServicesHover = e => {
    e.stopPropagation()
    if (serviceCount > 0) {
      setServicesAnchorEl(e.currentTarget)
    }
  }

  const handleServicesLeave = () => {
    setServicesAnchorEl(null)
  }

  const handleMoreActionsClick = e => {
    e.stopPropagation()
    onLocationAnchorElChange(e.currentTarget)
    onSelectedLocationForMenuChange(location)
  }

  return (
    <Box display="flex" alignItems="center" gap={0.5} onClick={e => e.stopPropagation()}>
      <Tooltip title={T.WORK_ORDER_STATUS} arrow placement="top">
        <IconButton size="small" onMouseEnter={handleWorkOrderStatusHover} onMouseLeave={handleWorkOrderStatusLeave}>
          <Badge badgeContent={workOrderCount} color="primary" max={99}>
            <ClipboardList size={24} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Tooltip title={T.SERVICES} arrow placement="top">
        <IconButton size="small" onMouseEnter={handleServicesHover} onMouseLeave={handleServicesLeave}>
          <Badge badgeContent={serviceCount} color="primary" max={99}>
            <SvgIcon>
              <CalendarWeek size={24} />
            </SvgIcon>
          </Badge>
        </IconButton>
      </Tooltip>

      <Tooltip title={T.MORE_ACTIONS} arrow placement="top">
        <IconButton size="small" onClick={handleMoreActionsClick} sx={{ color: 'text.secondary' }}>
          <MoreVertIcon />
        </IconButton>
      </Tooltip>

      <LocationServicesPopover
        anchorEl={servicesAnchorEl}
        onClose={handleServicesLeave}
        accountId={accountId}
        locationId={get(location, 'id')}
      />

      <UpcomingWorkOrdersPopover
        anchorEl={workOrdersAnchorEl}
        serviceType={RECURRENCE.RECURRING}
        workOrders={workOrders}
        isLoading={isWorkOrdersLoading}
        onClose={handleWorkOrderStatusLeave}
      />
    </Box>
  )
}

LocationActionsColumn.propTypes = {
  location: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    serviceCount: PropTypes.number,
  }),
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onLocationAnchorElChange: PropTypes.func.isRequired,
  onSelectedLocationForMenuChange: PropTypes.func.isRequired,
}

export default LocationActionsColumn
